import { useState } from 'react'
import { X, Sparkles, Check, AlertCircle, Loader2, History, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  useTriggerEnrichment,
  useApplyEnrichment,
  useContactEnrichments,
} from '@/hooks/use-contact-enrichment'
import type { Contact, ContactEnrichment } from '@/lib/types'
import { formatDistanceToNow } from 'date-fns'

interface EnrichmentDialogProps {
  contact: Contact
  onClose: () => void
}

const FIELD_LABELS: Record<string, string> = {
  email: 'Email',
  phone: 'Phone',
  company: 'Company',
  jobTitle: 'Job Title',
  location: 'Location',
  linkedinUrl: 'LinkedIn',
  twitterUrl: 'Twitter/X',
  githubUrl: 'GitHub',
  facebookUrl: 'Facebook',
  birthday: 'Birthday',
}

function currentValue(contact: Contact, field: string): string | null {
  if (field === 'linkedinUrl') return contact.links?.linkedin || contact.links?.linkedIn || null
  if (field === 'twitterUrl') return contact.links?.twitter || null
  if (field === 'githubUrl') return contact.links?.github || null
  if (field === 'facebookUrl') return contact.links?.facebook || null
  return (contact as unknown as Record<string, unknown>)[field] as string | null
}

export function EnrichmentDialog({ contact, onClose }: EnrichmentDialogProps) {
  const [tab, setTab] = useState<'enrich' | 'history'>('enrich')
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set())
  const [activeEnrichment, setActiveEnrichment] = useState<ContactEnrichment | null>(null)

  const triggerEnrichment = useTriggerEnrichment()
  const applyEnrichment = useApplyEnrichment()
  const { data: enrichmentHistory } = useContactEnrichments(contact.id)

  const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ')

  const handleEnrich = async () => {
    const result = await triggerEnrichment.mutateAsync({ contactId: contact.id })
    if (result.status === 'completed' || result.status === 'partial') {
      setActiveEnrichment(result)
      // Pre-select fields that add new data
      const rawData = result.rawData as Record<string, unknown>
      const newFields = new Set<string>()
      for (const field of Object.keys(rawData)) {
        if (rawData[field] && !currentValue(contact, field)) {
          newFields.add(field)
        }
      }
      setSelectedFields(newFields)
    }
  }

  const handleApply = async () => {
    if (!activeEnrichment || selectedFields.size === 0) return
    await applyEnrichment.mutateAsync({
      contactId: contact.id,
      enrichmentId: activeEnrichment.id,
      fields: [...selectedFields],
    })
    onClose()
  }

  const handleViewEnrichment = (enrichment: ContactEnrichment) => {
    setActiveEnrichment(enrichment)
    setTab('enrich')
    const rawData = enrichment.rawData as Record<string, unknown>
    const newFields = new Set<string>()
    for (const field of Object.keys(rawData)) {
      if (rawData[field] && !currentValue(contact, field)) {
        newFields.add(field)
      }
    }
    setSelectedFields(newFields)
  }

  const toggleField = (field: string) => {
    setSelectedFields((prev) => {
      const next = new Set(prev)
      if (next.has(field)) next.delete(field)
      else next.add(field)
      return next
    })
  }

  const enrichedFields = activeEnrichment
    ? Object.entries(activeEnrichment.rawData as Record<string, unknown>).filter(
        ([, v]) => v !== null && v !== undefined,
      )
    : []

  const alreadyApplied = activeEnrichment?.appliedFields != null

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="bg-card fixed inset-x-4 top-[10%] z-50 mx-auto max-w-lg rounded-2xl shadow-2xl sm:inset-x-auto sm:w-full">
        {/* Header */}
        <div className="border-border flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-2">
            <Sparkles className="text-coral size-5" />
            <h2 className="text-lg font-semibold">Enrich: {fullName}</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="size-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-border flex border-b px-5">
          <button
            className={cn(
              'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              tab === 'enrich'
                ? 'border-coral text-coral'
                : 'text-muted-foreground hover:text-foreground border-transparent',
            )}
            onClick={() => setTab('enrich')}
          >
            <Sparkles className="mr-1.5 inline size-3.5" />
            Enrich
          </button>
          <button
            className={cn(
              'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              tab === 'history'
                ? 'border-coral text-coral'
                : 'text-muted-foreground hover:text-foreground border-transparent',
            )}
            onClick={() => setTab('history')}
          >
            <History className="mr-1.5 inline size-3.5" />
            History
            {enrichmentHistory && enrichmentHistory.length > 0 && (
              <span className="bg-muted text-muted-foreground ml-1.5 rounded-full px-1.5 py-0.5 text-xs">
                {enrichmentHistory.length}
              </span>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
          {tab === 'enrich' && (
            <>
              {!activeEnrichment && !triggerEnrichment.isPending && (
                <div className="flex flex-col items-center gap-4 py-8 text-center">
                  <div className="bg-coral-soft rounded-full p-4">
                    <Sparkles className="text-coral size-8" />
                  </div>
                  <div>
                    <p className="text-foreground font-medium">Auto-fill missing data</p>
                    <p className="text-muted-foreground mt-1 text-sm">
                      Search public records to find email, phone, company, social profiles, and more
                      for this contact.
                    </p>
                  </div>
                  <Button onClick={handleEnrich} className="gap-2">
                    <Sparkles className="size-4" />
                    Enrich Contact
                  </Button>
                </div>
              )}

              {triggerEnrichment.isPending && (
                <div className="flex flex-col items-center gap-3 py-8">
                  <Loader2 className="text-coral size-8 animate-spin" />
                  <p className="text-muted-foreground text-sm">Searching for data...</p>
                </div>
              )}

              {activeEnrichment && activeEnrichment.status === 'failed' && (
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                  <AlertCircle className="text-destructive size-8" />
                  <p className="text-muted-foreground text-sm">
                    {activeEnrichment.error || 'No matching data found'}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setActiveEnrichment(null)
                      handleEnrich()
                    }}
                  >
                    Try Again
                  </Button>
                </div>
              )}

              {activeEnrichment &&
                (activeEnrichment.status === 'completed' ||
                  activeEnrichment.status === 'partial') && (
                  <div className="space-y-3">
                    <p className="text-muted-foreground text-sm">
                      {alreadyApplied
                        ? 'These fields were found. Some have already been applied.'
                        : 'Select which fields to merge into this contact:'}
                    </p>
                    {enrichedFields.map(([field, value]) => {
                      const existing = currentValue(contact, field)
                      const isNew = !existing
                      const isDifferent = existing && existing !== value
                      const wasApplied = activeEnrichment.appliedFields?.includes(field)

                      return (
                        <label
                          key={field}
                          className={cn(
                            'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                            wasApplied
                              ? 'border-chartreuse bg-chartreuse-soft'
                              : selectedFields.has(field)
                                ? 'border-coral bg-coral-soft/40'
                                : 'border-border hover:border-outline',
                          )}
                        >
                          {!alreadyApplied && (
                            <input
                              type="checkbox"
                              className="accent-coral mt-0.5"
                              checked={selectedFields.has(field)}
                              onChange={() => toggleField(field)}
                            />
                          )}
                          {wasApplied && (
                            <Check className="bg-chartreuse text-chartreuse-foreground mt-0.5 size-4 rounded-full p-0.5" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-foreground text-sm font-medium">
                                {FIELD_LABELS[field] || field}
                              </span>
                              {isNew && (
                                <span className="tag-chartreuse rounded px-1.5 py-0.5 text-[10px] font-medium">
                                  NEW
                                </span>
                              )}
                              {isDifferent && (
                                <span className="tag-coral rounded px-1.5 py-0.5 text-[10px] font-medium">
                                  UPDATE
                                </span>
                              )}
                              {wasApplied && (
                                <span className="tag-chartreuse rounded px-1.5 py-0.5 text-[10px] font-medium">
                                  APPLIED
                                </span>
                              )}
                            </div>
                            <div className="mt-1 text-sm">
                              {existing && (
                                <span className="text-muted-foreground line-through">
                                  {existing}
                                </span>
                              )}
                              {existing && isDifferent && (
                                <ArrowRight className="text-muted-foreground mx-1.5 inline size-3" />
                              )}
                              <span className="text-foreground">{String(value)}</span>
                            </div>
                          </div>
                        </label>
                      )
                    })}
                  </div>
                )}
            </>
          )}

          {tab === 'history' && (
            <div className="space-y-2">
              {(!enrichmentHistory || enrichmentHistory.length === 0) && (
                <p className="text-muted-foreground py-8 text-center text-sm">
                  No enrichment history
                </p>
              )}
              {enrichmentHistory?.map((e) => (
                <button
                  key={e.id}
                  className="border-border hover:border-outline flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors"
                  onClick={() => handleViewEnrichment(e)}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground text-sm font-medium capitalize">
                        {e.provider}
                      </span>
                      <span
                        className={cn(
                          'rounded px-1.5 py-0.5 text-[10px] font-medium',
                          e.status === 'completed' && 'tag-chartreuse',
                          e.status === 'partial' && 'tag-coral',
                          e.status === 'failed' && 'bg-destructive/10 text-destructive',
                        )}
                      >
                        {e.status}
                      </span>
                      {e.appliedFields && e.appliedFields.length > 0 && (
                        <span className="text-muted-foreground text-[10px]">
                          {e.appliedFields.length} applied
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground mt-0.5 text-xs">
                      {formatDistanceToNow(new Date(e.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                  <ArrowRight className="text-muted-foreground size-4" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {tab === 'enrich' && activeEnrichment && !alreadyApplied && enrichedFields.length > 0 && (
          <div className="border-border flex items-center justify-end gap-2 border-t px-5 py-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              disabled={selectedFields.size === 0 || applyEnrichment.isPending}
              className="gap-2"
            >
              {applyEnrichment.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Check className="size-4" />
              )}
              Apply {selectedFields.size} field{selectedFields.size !== 1 ? 's' : ''}
            </Button>
          </div>
        )}
      </div>
    </>
  )
}
